import { AlertCircle, RefreshCw, LogIn } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { createPortal } from 'react-dom'

interface SessionExpiredModalProps {
  isOpen: boolean
  onRefresh: () => void
  onLogin: () => void
}

export function SessionExpiredModal({ isOpen, onRefresh, onLogin }: SessionExpiredModalProps) {
  if (!isOpen) return null

  return createPortal(
    <AnimatePresence>
      {/* z-[1000] so it sits ABOVE Radix dialogs/overlays (z-[300]) — otherwise
          a session expiry while a dialog is open (e.g. the check-in workspace)
          leaves the modal hidden behind the dialog and the user only sees a
          toast. The session-expired prompt must always be the top-most layer. */}
      <div className="fixed inset-0 z-[1000] flex items-center justify-center">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        />
        
        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="relative z-10 w-full max-w-md mx-4"
        >
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
            {/* Header with icon */}
            <div className="bg-gradient-to-br from-amber-500 to-orange-600 p-6 text-center">
              <div className="mx-auto w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mb-4">
                <AlertCircle className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-xl font-semibold text-white">
                Session Expired
              </h2>
            </div>
            
            {/* Content */}
            <div className="p-6">
              <p className="text-gray-600 text-center mb-6">
                Your session has expired due to inactivity. Please refresh the page or log in again to continue.
              </p>
              
              <div className="space-y-3">
                {/* Primary action - Refresh */}
                <button
                  onClick={onRefresh}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-heritage-blue text-white rounded-lg font-medium hover:bg-heritage-blue/90 transition-colors"
                >
                  <RefreshCw className="w-5 h-5" />
                  Refresh Page
                </button>
                
                {/* Secondary action - Login */}
                <button
                  onClick={onLogin}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                >
                  <LogIn className="w-5 h-5" />
                  Log In Again
                </button>
              </div>
              
              <p className="text-xs text-gray-400 text-center mt-4">
                If the problem persists, please clear your browser cache and try again.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  )
}
